/**
 * What a nested editor needs before it can render (ECH-387).
 *
 * The first fix guaranteed a *profile* existed. Six route/role combinations
 * still crashed, because several editors go one level deeper and dereference a
 * **role-specific section** the member may not have:
 *
 *   edit-expertise         localProfile.profile.specializations
 *   edit-interests         localProfile.profile.interests
 *   edit-location-sponsor  localProfile.profile.sponsorProfile.organizationalScope
 *
 * A mentee has no expertise, a sponsor has no interests, and nobody but a
 * sponsor has a `sponsorProfile`. The Profile UI already knows this — it renders
 * 25 edit paths and hides the sections that do not apply — so only a typed URL
 * or a reload reaches them. V2 profiles keep promoted interests and
 * specializations on the canonical profile, rather than on role rows.
 *
 * ## The part QA's fixtures could not show
 *
 * Returning to the profile is the honest outcome for a typed URL to a missing
 * role-specific editor, and it is what ECH-387 asks for.
 */

const section = (profile, role) => {
  if (role === "mentee_mentor") {
    return (
      profile?.profile?.mentorProfile ?? profile?.profile?.menteeProfile ?? null
    );
  }
  return profile?.profile?.[`${role}Profile`] ?? null;
};

const supportsRole = (profile, role) => {
  const roles = profile?.roles;
  const hasRole = (name) =>
    roles?.length ? roles.includes(name) : Boolean(section(profile, role));

  return (
    ((role === "mentor" || role === "mentee_mentor") && hasRole("mentor")) ||
    ((role === "mentee" || role === "mentee_mentor") && hasRole("mentee"))
  );
};

export const profileSectionRequirements = Object.freeze({
  /** Mentor-side expertise. */
  expertise: (profile, role) =>
    supportsRole(profile, role) &&
    Array.isArray(profile?.profile?.specializations),

  /** Mentee-side interests. */
  interests: (profile, role) =>
    supportsRole(profile, role) && Array.isArray(profile?.profile?.interests),

  /** The organisation record. Only sponsors have one. */
  sponsorProfile: (profile) => Boolean(profile?.profile?.sponsorProfile),
});

export default profileSectionRequirements;
